import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { MatAccordion } from '@angular/material/expansion';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { faGrip, faList } from '@fortawesome/free-solid-svg-icons';
import { ParticipantCourseList, ParticipantCourseQueryFilter, ParticipantCourseQueryFilterDefaultValues } from 'src/app/components/course/participant-course-list';
import { NoResultsComponent } from 'src/app/components/no-results/no-results.component';
import { AuthService } from 'src/app/services/auth.service';
import { CourseService } from 'src/app/services/course.service';
import { FileService } from 'src/app/services/file.service';
import { ParticipantThemeService } from 'src/app/services/participant-theme.service';
import { TagsService } from 'src/app/services/tag-service.service';
import { TranslationService } from 'src/app/services/translation.service';
import { LanguageService } from 'src/app/services/language.service';
import { ApplicationLanguages } from 'src/app/components/language/language';

@Component({
    selector: 'app-courses',
    templateUrl: './courses.component.html',
    styleUrls: ['./courses.component.scss'],
    standalone: false
})
export class CoursesComponent {
  courseList: ParticipantCourseList[] = [];
  totalRecords: number = 0;
  columnsToDisplay = [
    'name','description','program','enrollment', 'status',
  ]
  tags: any[] = [];
  participantCourseStatus: any[] = [];
  pageEvent!: PageEvent;
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild('noResults') noResults!: NoResultsComponent;
  @ViewChild('submitButton') submitButton !:ElementRef
  @ViewChild(MatAccordion) accordion!: MatAccordion;
  information: string = '';
  queryFilter: ParticipantCourseQueryFilter = ParticipantCourseQueryFilterDefaultValues();
  loggedInUserPublicId: string = '';
  participantClassByRole: string = '';
  gridIcon = faGrip;
  listIcon = faList;
  translations: any;
  llttfPage: boolean = false;
  hasRetiredCourse: boolean = false;
  courseLanguages: { id: number; description: string }[] = [];

  private readonly statusTranslationKeys: Record<number, string> = {
    1: 'notStartedText',
    2: 'inProgressText',
    3: 'completedText',
    4: 'favouriteText',
    5: 'retiredText',
  };

  private readonly tagTranslationKeys: Record<string, string> = {
    'Situations, Relationships & Problems': 'tagSituationsText',
    'Thoughts': 'tagThoughtsText',
    'Emotions': 'tagEmotionsText',
    'Physical Feelings': 'tagPhysicalFeelingsText',
    'Behaviours': 'tagBehavioursText',
  };


  constructor(public courseservice: CourseService,
    private fileservice: FileService,
    private tagservice: TagsService,
    private authservice: AuthService,
    private translationservice: TranslationService,
    private themeService: ParticipantThemeService,
    private languageService: LanguageService)
  { }

  async ngOnInit(): Promise<void> {
    this.loggedInUserPublicId = this.authservice.getUserLoginData().publicId;

    await this.tagservice.getActiveTags().subscribe({
      next: (data) => {
        this.tags=data;
      }
    });

    await this.courseservice.getParticipantCourseStatus().subscribe({
      next:(resp) => {
        this.participantCourseStatus = resp;
      }
    });

    await this.translationservice.getTranslation().subscribe({
      next: (translation) => {
        this.translations = translation;
      }
    });

    this.languageService.getApplicationLanguages().subscribe({
      next: (languages) => {
        this.courseLanguages = languages;
      }
    });

    this.themeService.setParticipantTheme();
    this.participantClassByRole = this.themeService.participantClassByRole;
    this.llttfPage = this.themeService.llttfPage;
    this.applyParticipantLanguageFilter();

    this.searchWithFilters();
  }

  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.searchWithFilters();
  }

  public openThumbnailImage(id: any): Promise<string> {
    return new Promise((resolve, reject) => {
      this.fileservice.downloadFile(id).subscribe({
        next: (response) => {
          resolve(response.url);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }


  private applyParticipantLanguageFilter(): void {
    this.queryFilter.CourseLanguage = this.languageService.getCurretnLang();
  }

  private searchWithFilters() {
    this.courseservice.getParticipantCourses(this.queryFilter, this.loggedInUserPublicId).subscribe({
      next: (data) => {
        let headers = data.headers.get('x-pagination');
        if (headers) {
          let parsedHeaders = JSON.parse(headers);
          this.totalRecords = parsedHeaders.TotalCount;
          this.noResults.setTotal(this.totalRecords);
        }
        this.courseList = data.body ? data.body : [];

        this.hasRetiredCourse = false;
        this.courseList.forEach(course => {
          if (course.courseThumbnailIconId) {
            this.openThumbnailImage(course.courseThumbnailIconId).then(url => {
              course.thumbnailUrl = url;
            }).catch(error => {
              console.log(error);
            });
          }

          if (course.retired) {
            this.hasRetiredCourse = true;
          }
        });
      },
      error: (error) => {
        console.log(error);
      },
      complete: () => { }
    });
  }

  @HostListener('document:keypress', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent): void {
	  if (event.key === 'Enter') {
		this.submitButton.nativeElement.click();
		event.preventDefault();
	  }
	}

  resetFilters() {
    this.queryFilter = ParticipantCourseQueryFilterDefaultValues();
    this.applyParticipantLanguageFilter();
    this.search();
  }

  search() {
    this.searchWithFilters();
  }

  handleFavouriteClick() {
    this.searchWithFilters();
  }

  getProgressPercent(course: ParticipantCourseList): number {
    const status = (course.enrollmentStatus || '').replace(/\s+/g, '');
    if (status === 'Completed') {
      return 100;
    }
    if (status === 'InProgress') {
      return 40;
    }
    return 0;
  }

  getProgressLabel(course: ParticipantCourseList): string {
    const status = (course.enrollmentStatus || '').replace(/\s+/g, '');
    if (status === 'Completed') {
      return this.translations?.['completedText'] || 'Completed';
    }
    if (status === 'InProgress') {
      return this.translations?.['inProgressText'] || 'In progress';
    }
    return this.translations?.['notStartedText'] || 'Not started';
  }

  getStatusLabel(status: { key: number; value: string }): string {
    const key = this.statusTranslationKeys[status.key];
    return (key && this.translations?.[key]) || status.value;
  }

  getTagLabel(tag: { tagName: string }): string {
    const key = this.tagTranslationKeys[tag.tagName];
    return (key && this.translations?.[key]) || tag.tagName;
  }

  getLanguageLabel(lang: { id: number; description: string }): string {
    if (lang.id === ApplicationLanguages.ENGLISH) {
      return this.translations?.['englishText'] || lang.description;
    }
    if (lang.id === ApplicationLanguages.FRENCH) {
      return this.translations?.['frenchText'] || lang.description;
    }
    return lang.description;
  }

}
